import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Star, MapPin, X, Search, Store, Globe, Phone, Mail, Check
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CATS = ["Sonorisation","Photo & Video","Traiteur","Decoration","Animation","Securite","Location materiel","Communication","Logistique","Autres"];

const emptyForm = { name:"", category:"Sonorisation", description:"", phone:"", email:"", website:"", city:"", rating:4, featured:false, services:[] };

export default function AdminMarketplacePage() {
  const { token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // null | "add" | "edit"
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [svcInput, setSvcInput] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/providers`, { headers: h });
      setProviders(r.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setModal("add"); setSvcInput(""); };
  const openEdit = (p) => { setForm({ name:p.name, category:p.category, description:p.description, phone:p.phone||"", email:p.email||"", website:p.website||"", city:p.city||"", rating:p.rating||0, featured:!!p.featured, services:p.services||[] }); setEditId(p.id); setModal("edit"); setSvcInput(""); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      if (modal === "add") {
        await axios.post(`${API}/api/admin/providers`, form, { headers: h });
        toast.success("Prestataire cree");
      } else {
        await axios.put(`${API}/api/admin/providers/${editId}`, form, { headers: h });
        toast.success("Prestataire modifie");
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce prestataire ?")) return;
    try { await axios.delete(`${API}/api/admin/providers/${id}`, { headers: h }); toast.success("Supprime"); load(); }
    catch { toast.error("Erreur"); }
  };

  const addService = () => {
    if (svcInput.trim() && !form.services.includes(svcInput.trim())) {
      setForm(f => ({ ...f, services: [...f.services, svcInput.trim()] }));
      setSvcInput("");
    }
  };

  const filtered = providers.filter(p => `${p.name} ${p.category} ${p.city}`.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-marketplace">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion Prestataires</h1>
          <p className="text-[#64748b] text-sm mt-1">{providers.length} prestataire(s) dans l'annuaire</p>
        </div>
        <Button data-testid="add-provider-btn" onClick={openAdd} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
          <Plus className="h-4 w-4 mr-2" /> Ajouter
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A5568]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" />
      </div>

      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center"><Store className="h-10 w-10 text-[#2A3441] mx-auto mb-3" /><p className="text-[#64748b]">Aucun prestataire</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#1E293B] text-[#64748b] text-xs uppercase tracking-wider">
              <th className="text-left p-3 pl-4">Nom</th>
              <th className="text-left p-3 hidden sm:table-cell">Categorie</th>
              <th className="text-left p-3 hidden md:table-cell">Ville</th>
              <th className="text-left p-3 hidden md:table-cell">Note</th>
              <th className="text-left p-3 hidden lg:table-cell">Services</th>
              <th className="text-right p-3 pr-4">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-[#1E293B]/50 hover:bg-[#0F1420] transition-colors">
                  <td className="p-3 pl-4">
                    <div className="font-medium text-white flex items-center gap-2">{p.name}{p.featured && <span className="text-[10px] bg-[#F59E0B]/20 text-[#F59E0B] px-1.5 py-0.5 rounded">Top</span>}</div>
                    <div className="text-xs text-[#64748b] sm:hidden">{p.category}</div>
                  </td>
                  <td className="p-3 text-[#0099CC] text-xs hidden sm:table-cell">{p.category}</td>
                  <td className="p-3 text-[#94A3B8] hidden md:table-cell">{p.city || "—"}</td>
                  <td className="p-3 hidden md:table-cell"><div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= p.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#2A3441]"}`} />)}</div></td>
                  <td className="p-3 hidden lg:table-cell text-[#64748b] text-xs">{(p.services||[]).slice(0,3).join(", ")}{(p.services||[]).length > 3 ? ` +${p.services.length-3}` : ""}</td>
                  <td className="p-3 pr-4 text-right">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#0099CC] hover:text-white p-1.5 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 p-1.5 rounded transition-colors ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Add/Edit */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0F1420] border border-[#2A3441] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="provider-modal">
            <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-white">{modal === "add" ? "Nouveau prestataire" : "Modifier le prestataire"}</h2><button onClick={() => setModal(null)}><X className="h-5 w-5 text-[#64748b] hover:text-white" /></button></div>

            <div className="space-y-3">
              <div><label className="text-xs text-[#64748b] mb-1 block">Nom *</label><Input data-testid="provider-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
              <div><label className="text-xs text-[#64748b] mb-1 block">Categorie</label>
                <select data-testid="provider-category" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full bg-[#151B2B] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-[#64748b] mb-1 block">Description</label><textarea data-testid="provider-desc" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full bg-[#151B2B] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm h-20 resize-none" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#64748b] mb-1 block">Ville</label><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
                <div><label className="text-xs text-[#64748b] mb-1 block">Telephone</label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#64748b] mb-1 block">Email</label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
                <div><label className="text-xs text-[#64748b] mb-1 block">Site web</label><Input value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#64748b] mb-1 block">Note (1-5)</label>
                  <div className="flex gap-1">{[1,2,3,4,5].map(i => <button key={i} onClick={() => setForm(f => ({...f, rating: i}))} className="p-0.5"><Star className={`h-5 w-5 ${i <= form.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#2A3441]"}`} /></button>)}</div>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <button onClick={() => setForm(f => ({...f, featured: !f.featured}))} className={`h-5 w-5 rounded border ${form.featured ? "bg-[#F59E0B] border-[#F59E0B]" : "border-[#2A3441]"} flex items-center justify-center`}>
                      {form.featured && <Check className="h-3 w-3 text-black" />}
                    </button>
                    <span className="text-sm text-[#94A3B8]">Recommande (Top)</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#64748b] mb-1 block">Services</label>
                <div className="flex gap-2 mb-2"><Input value={svcInput} onChange={e => setSvcInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addService())} placeholder="Ajouter un service..." className="bg-[#151B2B] border-[#2A3441] text-white flex-1" /><Button onClick={addService} size="sm" variant="outline" className="border-[#2A3441] text-[#94A3B8]">+</Button></div>
                <div className="flex flex-wrap gap-1.5">{form.services.map(s => <span key={s} className="bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] text-xs px-2 py-1 rounded-lg flex items-center gap-1">{s}<button onClick={() => setForm(f => ({...f, services: f.services.filter(x => x !== s)}))}><X className="h-3 w-3" /></button></span>)}</div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button data-testid="save-provider-btn" onClick={save} disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white flex-1">{saving ? "Enregistrement..." : modal === "add" ? "Creer" : "Enregistrer"}</Button>
              <Button variant="outline" onClick={() => setModal(null)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
